import { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, api } from "../App";
import PageContent from "../components/PageContent";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { OverdueMaintenanceCard } from "../components/OverdueMaintenanceCard";
import { 
  Users, 
  Calendar, 
  DollarSign, 
  TrendingUp,
  Clock,
  Plus,
  ArrowRight,
  Sparkles
} from "lucide-react";
import { formatDate } from "../utils/formatters";

export default function Dashboard() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const currentUser = location.state?.user || user;

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get("/dashboard/stats");
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleAppointment = (patient) => {
    // Navegar para página de agendamentos com dados do paciente
    navigate('/appointments', {
      state: {
        openNewAppointment: true,
        selectedPatient: patient
      }
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const statCards = [
    {
      title: "Total de Pacientes",
      value: stats?.total_patients || 0,
      icon: <Users className="w-5 h-5" />,
      color: "bg-teal-500",
      link: "/patients"
    },
    {
      title: "Consultas Hoje",
      value: stats?.today_appointments || 0,
      icon: <Calendar className="w-5 h-5" />,
      color: "bg-blue-500",
      link: "/appointments"
    },
    {
      title: "Pagamentos Pendentes",
      value: stats?.pending_payments || 0,
      icon: <Clock className="w-5 h-5" />,
      color: "bg-amber-500",
      link: "/finances"
    }
  ];

  const quickActions = [
    { title: "Novo Paciente", icon: <Users className="w-4 h-4" />, link: "/patients?action=new" },
    { title: "Agendar Consulta", icon: <Calendar className="w-4 h-4" />, link: "/appointments?action=new" },
    { title: "Chat IA", icon: <Sparkles className="w-4 h-4" />, link: "/chat" },
    { title: "Novo Orçamento", icon: <DollarSign className="w-4 h-4" />, link: "/finances?action=new" }
  ];

  return (
    <div className="flex min-h-screen bg-slate-50">
      <PageContent>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 mb-2" data-testid="dashboard-title">
            Olá, {currentUser?.name?.split(' ')[0] || 'Usuário'}!
          </h1>
          <p className="text-slate-600">Veja o resumo da sua clínica hoje.</p>
        </div>

        {/* Stats Grid - 3 cards principais + 1 card de pacientes atrasados */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-8">
          {statCards.map((stat, index) => (
            <Link to={stat.link} key={index}>
              <Card className="bg-white border border-slate-100 hover:shadow-lg transition-all cursor-pointer card-hover" data-testid={`stat-card-${index}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-10 h-10 rounded-xl ${stat.color} flex items-center justify-center text-white`}>
                      {stat.icon}
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400" />
                  </div>
                  <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold text-slate-900">
                    {loading ? "..." : stat.value}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
          
          {/* Card de Pacientes Atrasados */}
          <OverdueMaintenanceCard onScheduleAppointment={handleScheduleAppointment} />
        </div>

        {/* Quick Actions & Upcoming Appointments */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <Card className="bg-white border border-slate-100 lg:col-span-1">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg font-semibold text-slate-900">Ações Rápidas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {quickActions.map((action, index) => (
                <Link to={action.link} key={index}>
                  <Button 
                    variant="outline" 
                    className="w-full justify-start h-12 border-slate-200 hover:border-teal-500 hover:bg-teal-50 hover:text-teal-700"
                    data-testid={`quick-action-${index}`}
                  >
                    <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center mr-3">
                      {action.icon}
                    </div>
                    {action.title}
                  </Button>
                </Link>
              ))}
            </CardContent>
          </Card>

          {/* Upcoming Appointments */}
          <Card className="bg-white border border-slate-100 lg:col-span-2">
            <CardHeader className="pb-4 flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold text-slate-900">Próximas Consultas</CardTitle>
              <Link to="/appointments">
                <Button variant="ghost" size="sm" className="text-teal-700 hover:text-teal-800" data-testid="view-all-appointments">
                  Ver todas
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse"></div>
                  ))}
                </div>
              ) : stats?.upcoming_appointments?.length > 0 ? (
                <div className="space-y-3">
                  {stats.upcoming_appointments.map((apt, index) => (
                    <div 
                      key={apt.appointment_id || index} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                      data-testid={`appointment-${index}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-teal-700" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{apt.patient_name || "Paciente"}</p>
                          <p className="text-sm text-slate-500">{apt.type}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-slate-900">{apt.time}</p>
                        <p className="text-sm text-slate-500">{formatDate(apt.date)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">Nenhuma consulta agendada</p>
                  <Link to="/appointments?action=new">
                    <Button className="mt-4 bg-teal-700 hover:bg-teal-800" data-testid="new-appointment-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Agendar Consulta
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* AI Features Banner */}
        <Card className="mt-6 gradient-ai text-white border-0 overflow-hidden">
          <CardContent className="p-6 lg:p-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                  <Sparkles className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-1">Recursos de IA</h3>
                  <p className="text-white/80">Use inteligência artificial para triagem, análise de imagens e relatórios.</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Link to="/chat">
                  <Button variant="secondary" className="bg-white text-teal-700 hover:bg-white/90" data-testid="ai-chat-btn">
                    Chat Triagem
                  </Button>
                </Link>
                <Link to="/image-analysis">
                  <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="ai-analysis-btn">
                    Análise de Imagem
                  </Button>
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </PageContent>
    </div>
  );
}
